import React, { useState, useRef, useEffect } from "react";
import { Camera } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Pencil, Trash2, X } from "lucide-react";
import { FaFilePdf } from "react-icons/fa6";
import { FaFileExcel } from "react-icons/fa6";
import { BiSolidFileJpg } from "react-icons/bi";
import { IoDocumentText } from "react-icons/io5";
import { BiSolidFilePng } from "react-icons/bi";
import { ContentLayout } from "../components/admin-panel/content-layout";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import User from "../assets/images/user.png";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import UserService from "../services/user/user";
import { toastError, toastSuccess } from "../components/ToastNotification";
import SuperAdminService from "../services/superAdmin";
import { setUser } from "../store/slices/superAdmin";
import { useNavigate } from "react-router";
import ChatService from "../services/chat/chat";
import { FaVideo } from "react-icons/fa";
import Loader from "../assets/images/loader.gif";
import NoFiles from "../assets/images/NoFiles.svg";

const Documents = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [companyId, setCompanyId] = useState("");
  const user = useSelector((state) => state?.superAdmin?.user);
  console.log("🚀  uservvvvv:", user)
  const [fileDeleteLoading, setFileDeleteLoading] = useState("");
  const [files, setFiles] = useState("");

  const renderFilePreview = (file) => {
    if (!file) return null;

    const fileName = file?.substring(file.lastIndexOf("/") + 1).toLowerCase();
    if (fileName.endsWith("pdf")) {
      return <FaFilePdf className="text-[#CC0000] text-2xl" />;
    } else if (fileName.endsWith("doc") || fileName.endsWith("docx")) {
      return <IoDocumentText className="text-[#3980FB] text-2xl" />;
    } else if (fileName.endsWith("xlsx")) {
      return <FaFileExcel className="text-[#00733B] text-2xl" />;
    } else if (fileName.endsWith("jpg")) {
      return <BiSolidFileJpg className="text-[#2C7FC3] text-2xl" />;
    } else if (fileName.endsWith("png")) {
      return <BiSolidFilePng className="text-[#F05C58] text-2xl" />;
    } else {
      return <IoDocumentText className="text-2xl" />; // Default icon for other file types
    }
  };

  const handleGoBack = () => {
    navigate(-1); // This will navigate to the previous page
  };

  const handleDeleteFile = async (id) => {
    setFileDeleteLoading(id);
    try {
      let response;
      if (user.admintype === "IndividualAdmin") {
        response = await ChatService?.deleteindividualfile(user._id, id);
      } else {
        response = await ChatService?.deleteFile(companyId, id);
      }
      if (response) { 
        setFileDeleteLoading("");
        getCompany();
        toastSuccess(response?.data?.message || "Deleted successfully");
      }
    } catch (error) {
      toastError(error?.response?.data?.message);
    }
  };

  const getCompany = async () => {
    setIsLoading(true);
    try {
      let response;
      if (user.admintype === "IndividualAdmin") {
        response = await UserService?.getIndividualdock()
      } else {
        response = await UserService?.getCompany();
      }
      if (response) {
        setCompanyId(response.data.data?._id);
        setFiles(response.data.data?.document);
        setIsLoading(false);
      }
    } catch (error) {
      toastError(error?.response?.data?.message);
    }
  };

  useEffect(() => {
    if (user) {
      getCompany();
    }
  }, [user]);

  return (
    <ContentLayout>
      {isLoading ? (
        <div className="flex justify-center items-center h-[calc(100vh-180px)]">
          <img src={Loader} alt="Loader" className="w-20" />
        </div>
      ) : (
        <Card
          className={`min-h-[calc(100vh-178px)] h-full ${
            files && files.length > 0 ? "" : "flex items-center justify-center"
          }`}
        >
          {files && files.length > 0 ? (
            <div>
              <CardHeader>Files:</CardHeader>
              <div className="px-6 w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {files?.map((file, index) => {
                  const extension = file.url.split(".").pop().toLowerCase(); // Extract and normalize the file extension
                  const isVideo = [
                    "mp4",
                    "avi",
                    "mkv",
                    "mov",
                    "wmv",
                    "flv",
                  ].includes(extension);

                  // If it's a video, skip rendering it
                  // if (isVideo) return null;
                  return (
                    <div
                      key={index}
                      className={`relative flex items-center justify-center gap-2 p-4 border rounded-md uppercase cursor-pointer ${
                        extension === "pdf"
                          ? "border-[#CC0000]"
                          : extension === "doc" || extension === "docx"
                          ? "border-[#3980FB]"
                          : extension === "xlsx" || extension === "xls"
                          ? "border-[#00733B]"
                          : extension === "jpg" || extension === "jpeg"
                          ? "border-[#2C7FC3]"
                          : extension === "mp4" ||
                            extension === "avi" ||
                            extension === "mkv" ||
                            extension === "mov" ||
                            extension === "wmv" ||
                            extension === "flv" // For video formats
                          ? "border-primary/50" // Set a distinct border color for videos (orange)
                          : "border-primary/50" // Default border for other file types
                      }`}
                    >
                      {/* Render video icon if it's a video file */}
                      <div
                        className="flex gap-2"
                        onClick={() => {
                          if (
                            extension === "mp4" ||
                            extension === "avi" ||
                            extension === "mkv" ||
                            extension === "mov" ||
                            extension === "wmv" ||
                            extension === "flv"
                          ) {
                            // Open video in a new tab for playback
                            window.open(file.url, "_blank");
                          } else if (
                            extension === "xlsx" ||
                            extension === "xls"
                          ) {
                            // For Excel files, trigger a download
                            const link = document.createElement("a");
                            link.href = file;
                            link.download = file.url.substring(
                              file.url.lastIndexOf("/") + 1
                            );
                            link.click();
                          } else {
                            // Open other file types (pdf, jpg, etc.) in a new tab
                            window.open(file.url, "_blank");
                          }
                        }}
                      >
                        <div className="">
                          {extension === "mp4" ||
                          extension === "avi" ||
                          extension === "mkv" ? (
                            <FaVideo size={30} />
                          ) : (
                            // <i className="fas fa-video fa-2x" /> // Video icon for video files
                            renderFilePreview(file.url) // Render file preview for non-video files
                          )}
                        </div>
                        <span className="break-all line-clamp-1">
                          {file.name}
                        </span>
                      </div>

                      {/* Optional: Add a remove button */}
                      <div
                        className={`absolute -right-3 -top-3 border-white/20 border rounded-full cursor-pointer ${
                          fileDeleteLoading
                            ? "hover:none"
                            : "hover:bg-[#CC0000] hover:border-[#CC0000]"
                        } duration-200 p-1 bg-[#070710]`}
                        onClick={() => handleDeleteFile(file.url)}
                      >
                        {fileDeleteLoading == file ? (
                          <div className="w-5 h-5">
                            <img src={Loader} alt="Loader" />
                          </div>
                        ) : (
                          <X className="h-4 w-4" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="grid gap-4 justify-center items-center font-bold h-full">
              <img src={NoFiles} alt="" />
              <div className="flex gap-4 items-center justify-center">
                <Button
                  className="bg-primary hover:bg-btnHover text-white text-base min-w-52"
                  onClick={handleGoBack}
                  variant="outline"
                >
                  Go Back
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </ContentLayout>
  );
};

export default Documents;
